import React, { useEffect, useMemo, useState } from 'react'
import {
  Star, Send, Save, Eye, Loader2, Users, AlertTriangle,
  CheckCircle, XCircle, X, Mail, Link as LinkIcon, RotateCcw, Clock,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'
import { useAuth } from '../../hooks/useAuth'

interface EvaluationSettings {
  id: string
  form_url: string | null
  newsletter_url: string
  default_subject: string
  default_body: string
}

interface SessionAggregate {
  id: string
  year: number
  periode: string
  center_id: string
  center_name: string
}

interface PreviewRecipient {
  email: string
  prenom: string | null
  nom: string | null
  kids_count: number
  centers: string[]
  presence_days?: number
}

interface FailedRecipient {
  email: string
  prenom: string | null
  nom: string | null
  error: string
}

interface CampaignMeta {
  id: string
  created_at: string
  year: number
  periode: string
  center_name: string | null
  subject: string
  total_recipients: number
  status: string
}

interface Progress {
  done: number
  total: number
  success: number
  failure: number
}

const DAILY_LIMIT = 1500
const BATCH_SIZE = 5

export function EvaluationCampaign() {
  const { userProfile } = useAuth()
  const isAdmin = userProfile?.role === 'admin'

  const [loading, setLoading] = useState(true)
  const [settings, setSettings] = useState<EvaluationSettings | null>(null)
  const [sessionsAgg, setSessionsAgg] = useState<SessionAggregate[]>([])
  const [centers, setCenters] = useState<{ id: string; name: string }[]>([])

  const [year, setYear] = useState<number | ''>('')
  const [periode, setPeriode] = useState<string>('')
  const [centerId, setCenterId] = useState<string>('')
  const [formUrl, setFormUrl] = useState<string>('')
  const [newsletterUrl, setNewsletterUrl] = useState<string>('')
  const [subject, setSubject] = useState<string>('')
  const [body, setBody] = useState<string>('')

  const [mode, setMode] = useState<'bulk' | 'single'>('bulk')
  const [singleEmail, setSingleEmail] = useState<string>('')

  const [previewing, setPreviewing] = useState(false)
  const [previewRecipients, setPreviewRecipients] = useState<PreviewRecipient[] | null>(null)
  const [previewFilterLabel, setPreviewFilterLabel] = useState<string>('')
  const [previewCenterName, setPreviewCenterName] = useState<string | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [failures, setFailures] = useState<FailedRecipient[]>([])
  const [currentCampaignId, setCurrentCampaignId] = useState<string | null>(null)
  const [lastCampaign, setLastCampaign] = useState<CampaignMeta | null>(null)

  const [singleResult, setSingleResult] = useState<{ recipient: string; matched_user: boolean } | null>(null)

  const [savingDefaults, setSavingDefaults] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [showSendConfirm, setShowSendConfirm] = useState(false)

  const [resending, setResending] = useState<Set<string>>(new Set())
  const [resendingAll, setResendingAll] = useState(false)

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    setLoading(true)
    try {
      // Le formulaire d'evaluation est celui des stages : pas de journees pedagogiques (JPA13)
      const jpa = await stagesDesJpa()
      const [settingsRes, sessionsRes, centersRes] = await Promise.all([
        supabase.from('evaluation_settings').select('*').maybeSingle(),
        sansJpa(supabase
          .from('sessions')
          .select('id, start_date, periode, center_id, center:centers(id, name)')
          .eq('active', true), jpa),
        supabase.from('centers').select('id, name').eq('active', true).order('name'),
      ])

      if (settingsRes.error) throw settingsRes.error
      if (sessionsRes.error) throw sessionsRes.error
      if (centersRes.error) throw centersRes.error

      const s = settingsRes.data as EvaluationSettings | null
      if (s) {
        setSettings(s)
        setFormUrl(s.form_url || '')
        setNewsletterUrl(s.newsletter_url || 'https://app.eclosion.be')
        setSubject(s.default_subject)
        setBody(s.default_body)
      }

      const rows = (sessionsRes.data || []) as Array<{
        id: string
        start_date: string
        periode: string
        center_id: string
        center: { id: string; name: string } | null
      }>

      const aggMap = new Map<string, SessionAggregate>()
      for (const row of rows) {
        if (!row.start_date || !row.periode) continue
        const yr = new Date(row.start_date).getFullYear()
        const key = `${yr}::${row.periode}::${row.center_id}`
        if (!aggMap.has(key)) {
          aggMap.set(key, {
            id: key,
            year: yr,
            periode: row.periode,
            center_id: row.center_id,
            center_name: row.center?.name || 'Centre',
          })
        }
      }
      setSessionsAgg(Array.from(aggMap.values()))
      setCenters(centersRes.data || [])

      await loadLastCampaign()
    } catch (err) {
      console.error('Error loading evaluation campaign data:', err)
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setLoading(false)
    }
  }

  const loadLastCampaign = async () => {
    try {
      const { data: campaigns, error } = await supabase
        .from('evaluation_campaigns')
        .select('id, created_at, year, periode, center_name, subject, total_recipients, status')
        .order('created_at', { ascending: false })
        .limit(1)
      if (error) throw error
      if (!campaigns || campaigns.length === 0) {
        setLastCampaign(null)
        return
      }
      const last = campaigns[0] as CampaignMeta
      setLastCampaign(last)

      const { data: failedRows } = await supabase
        .from('evaluation_campaign_recipients')
        .select('email, prenom, nom, error_message')
        .eq('campaign_id', last.id)
        .eq('status', 'failed')
      if (failedRows) {
        setFailures(
          (failedRows as Array<{ email: string; prenom: string | null; nom: string | null; error_message: string | null }>)
            .map(r => ({
              email: r.email,
              prenom: r.prenom,
              nom: r.nom,
              error: r.error_message || 'Erreur inconnue',
            }))
        )
        setCurrentCampaignId(last.id)
      }
    } catch (err) {
      console.error('loadLastCampaign failed:', err)
    }
  }

  const availableYears = useMemo(() => {
    const set = new Set<number>()
    for (const s of sessionsAgg) set.add(s.year)
    return Array.from(set).sort((a, b) => b - a)
  }, [sessionsAgg])

  const availablePeriodes = useMemo(() => {
    if (!year) return [] as string[]
    const set = new Set<string>()
    for (const s of sessionsAgg) {
      if (s.year === year) set.add(s.periode)
    }
    return Array.from(set).sort()
  }, [sessionsAgg, year])

  const filteredCenters = useMemo(() => {
    if (!year || !periode) return centers
    const usedIds = new Set(
      sessionsAgg
        .filter(s => s.year === year && s.periode === periode)
        .map(s => s.center_id),
    )
    return centers.filter(c => usedIds.has(c.id))
  }, [centers, sessionsAgg, year, periode])

  const canSave = isAdmin && !savingDefaults && !!settings
  const canPreview =
    mode === 'bulk' && !!year && !!periode && !previewing && !sending
  const canSend =
    !sending &&
    (mode === 'single'
      ? isValidEmail(singleEmail) && subject.trim() && body.trim() && isValidUrl(formUrl) && isValidUrl(newsletterUrl)
      : !!year && !!periode && subject.trim() && body.trim() && isValidUrl(formUrl) && isValidUrl(newsletterUrl))

  const handleSaveDefaults = async () => {
    if (!settings) return
    setSavingDefaults(true)
    setErrorMsg(null)
    try {
      const { error } = await supabase
        .from('evaluation_settings')
        .update({
          form_url: formUrl.trim() || null,
          newsletter_url: newsletterUrl.trim() || 'https://app.eclosion.be',
          default_subject: subject.trim() || 'Votre avis sur les plaines Éclosion',
          default_body: body,
          updated_at: new Date().toISOString(),
          updated_by: userProfile?.id ?? null,
        })
        .eq('id', settings.id)
      if (error) throw error
      alert('Valeurs par défaut enregistrées.')
    } catch (err) {
      console.error(err)
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setSavingDefaults(false)
    }
  }

  const getToken = async (): Promise<string> => {
    const session = (await supabase.auth.getSession()).data.session
    const token = session?.access_token
    if (!token) throw new Error('Session expirée, veuillez vous reconnecter.')
    return token
  }

  const callEdge = async (token: string, payload: unknown): Promise<any> => {
    const resp = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-evaluation-email`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    )
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok) throw new Error(data?.error || `Erreur HTTP ${resp.status}`)
    return data
  }

  const handlePreview = async () => {
    setPreviewing(true)
    setErrorMsg(null)
    setPreviewRecipients(null)
    try {
      const token = await getToken()
      const data = await callEdge(token, {
        mode: 'preview',
        year,
        periode,
        center_id: centerId || null,
      })
      setPreviewRecipients(data.recipients || [])
      setPreviewFilterLabel(data.filter_label || '')
      setPreviewCenterName(data.center_name || null)
      setShowPreviewModal(true)
    } catch (err) {
      console.error(err)
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setPreviewing(false)
    }
  }

  const openSendConfirm = () => {
    setErrorMsg(null)
    setShowSendConfirm(true)
  }

  const runTargetedBatches = async (
    token: string,
    campaignId: string | null,
    recipients: PreviewRecipient[] | FailedRecipient[],
    filterLabel: string,
  ): Promise<{ success: number; failure: number; failures: FailedRecipient[] }> => {
    const total = recipients.length
    setProgress({ done: 0, total, success: 0, failure: 0 })
    const cumulativeFailures: FailedRecipient[] = []
    let successCount = 0
    let failureCount = 0

    for (let i = 0; i < total; i += BATCH_SIZE) {
      const chunk = recipients.slice(i, i + BATCH_SIZE)
      const isFinal = i + BATCH_SIZE >= total
      const cumulativeTotalSoFar = successCount + failureCount + chunk.length
      const cumulativeSuccessProjected = successCount
      const cumulativeFailureProjected = failureCount

      let data: any
      try {
        data = await callEdge(token, {
          mode: 'targeted',
          recipient_emails: chunk.map(r => r.email),
          form_url: formUrl.trim(),
          newsletter_url: newsletterUrl.trim(),
          subject: subject.trim(),
          body_template: body,
          is_final: isFinal,
          filter_label: filterLabel || 'campagne',
          cumulative_total: total,
          cumulative_success: cumulativeSuccessProjected,
          cumulative_failure: cumulativeFailureProjected,
        })
      } catch (err) {
        for (const r of chunk) {
          const failed: FailedRecipient = {
            email: r.email,
            prenom: (r as PreviewRecipient).prenom ?? (r as FailedRecipient).prenom ?? null,
            nom: (r as PreviewRecipient).nom ?? (r as FailedRecipient).nom ?? null,
            error: err instanceof Error ? err.message : 'Erreur inconnue',
          }
          cumulativeFailures.push(failed)
          failureCount += 1
          if (campaignId) {
            await supabase
              .from('evaluation_campaign_recipients')
              .update({ status: 'failed', error_message: failed.error })
              .eq('campaign_id', campaignId)
              .eq('email', failed.email)
          }
        }
        setProgress({ done: successCount + failureCount, total, success: successCount, failure: failureCount })
        continue
      }

      const results = Array.isArray(data.results) ? data.results : []
      for (const r of results) {
        const email = String(r.email)
        const recip = chunk.find(c => c.email === email)
        const prenom = (recip as PreviewRecipient)?.prenom ?? (recip as FailedRecipient)?.prenom ?? null
        const nom = (recip as PreviewRecipient)?.nom ?? (recip as FailedRecipient)?.nom ?? null

        if (r.success) {
          successCount += 1
          if (campaignId) {
            await supabase
              .from('evaluation_campaign_recipients')
              .update({ status: 'sent', error_message: null, sent_at: new Date().toISOString() })
              .eq('campaign_id', campaignId)
              .eq('email', email)
          }
        } else {
          failureCount += 1
          const failed: FailedRecipient = {
            email,
            prenom,
            nom,
            error: r.error || 'Erreur inconnue',
          }
          cumulativeFailures.push(failed)
          if (campaignId) {
            await supabase
              .from('evaluation_campaign_recipients')
              .update({ status: 'failed', error_message: failed.error })
              .eq('campaign_id', campaignId)
              .eq('email', email)
          }
        }
      }

      setProgress({ done: successCount + failureCount, total, success: successCount, failure: failureCount })
    }

    return { success: successCount, failure: failureCount, failures: cumulativeFailures }
  }

  const handleSend = async () => {
    setShowSendConfirm(false)
    setSending(true)
    setErrorMsg(null)
    setSingleResult(null)
    try {
      const token = await getToken()

      if (mode === 'single') {
        const data = await callEdge(token, {
          mode: 'single',
          single_email: singleEmail.trim(),
          form_url: formUrl.trim(),
          newsletter_url: newsletterUrl.trim(),
          subject: subject.trim(),
          body_template: body,
        })
        setSingleResult({
          recipient: data.recipient,
          matched_user: data.matched_user,
        })
        return
      }

      let recipients = previewRecipients
      let filterLabel = previewFilterLabel
      let centerName = previewCenterName
      if (!recipients || recipients.length === 0) {
        const previewData = await callEdge(token, {
          mode: 'preview',
          year,
          periode,
          center_id: centerId || null,
        })
        recipients = previewData.recipients || []
        filterLabel = previewData.filter_label || ''
        centerName = previewData.center_name || null
        setPreviewRecipients(recipients)
        setPreviewFilterLabel(filterLabel)
        setPreviewCenterName(centerName)
      }

      if (!recipients || recipients.length === 0) {
        setErrorMsg('Aucun destinataire trouvé pour ces critères.')
        return
      }

      const { data: campaign, error: campaignErr } = await supabase
        .from('evaluation_campaigns')
        .insert({
          created_by: userProfile?.id ?? null,
          year: Number(year),
          periode,
          center_id: centerId || null,
          center_name: centerName,
          subject: subject.trim(),
          body_template: body,
          form_url: formUrl.trim(),
          newsletter_url: newsletterUrl.trim(),
          total_recipients: recipients.length,
          status: 'in_progress',
        })
        .select('id')
        .maybeSingle()
      if (campaignErr || !campaign) throw new Error(campaignErr?.message || 'Impossible de créer la campagne')
      const campaignId = campaign.id as string
      setCurrentCampaignId(campaignId)

      const recipientRows = recipients.map(r => ({
        campaign_id: campaignId,
        email: r.email,
        prenom: r.prenom,
        nom: r.nom,
        status: 'pending' as const,
      }))
      for (let i = 0; i < recipientRows.length; i += 500) {
        const slice = recipientRows.slice(i, i + 500)
        const { error: insertErr } = await supabase
          .from('evaluation_campaign_recipients')
          .insert(slice)
        if (insertErr) throw insertErr
      }

      setFailures([])

      const outcome = await runTargetedBatches(token, campaignId, recipients, filterLabel)
      setFailures(outcome.failures)

      const finalStatus = outcome.failure > 0 ? 'partial_failure' : 'completed'
      await supabase
        .from('evaluation_campaigns')
        .update({ status: finalStatus })
        .eq('id', campaignId)

      await loadLastCampaign()
    } catch (err) {
      console.error(err)
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setSending(false)
    }
  }

  const handleResendOne = async (target: FailedRecipient) => {
    if (!isValidUrl(formUrl) || !isValidUrl(newsletterUrl) || !subject.trim() || !body.trim()) {
      setErrorMsg('Vérifiez les champs objet, corps, lien du formulaire et newsletter avant de renvoyer.')
      return
    }
    setErrorMsg(null)
    setResending(prev => {
      const next = new Set(prev)
      next.add(target.email)
      return next
    })
    try {
      const token = await getToken()
      const data = await callEdge(token, {
        mode: 'targeted',
        recipient_emails: [target.email],
        form_url: formUrl.trim(),
        newsletter_url: newsletterUrl.trim(),
        subject: subject.trim(),
        body_template: body,
        is_final: false,
      })
      const result = Array.isArray(data.results) ? data.results[0] : null
      if (result?.success) {
        if (currentCampaignId) {
          await supabase
            .from('evaluation_campaign_recipients')
            .update({ status: 'sent', error_message: null, sent_at: new Date().toISOString() })
            .eq('campaign_id', currentCampaignId)
            .eq('email', target.email)
        }
        setFailures(prev => prev.filter(f => f.email !== target.email))
      } else {
        const errorMsgText = result?.error || 'Erreur inconnue'
        if (currentCampaignId) {
          await supabase
            .from('evaluation_campaign_recipients')
            .update({ status: 'failed', error_message: errorMsgText })
            .eq('campaign_id', currentCampaignId)
            .eq('email', target.email)
        }
        setFailures(prev => prev.map(f => f.email === target.email ? { ...f, error: errorMsgText } : f))
      }
    } catch (err) {
      console.error(err)
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setResending(prev => {
        const next = new Set(prev)
        next.delete(target.email)
        return next
      })
    }
  }

  const handleResendAllFailures = async () => {
    if (failures.length === 0) return
    if (!isValidUrl(formUrl) || !isValidUrl(newsletterUrl) || !subject.trim() || !body.trim()) {
      setErrorMsg('Vérifiez les champs objet, corps, lien du formulaire et newsletter avant de renvoyer.')
      return
    }
    setResendingAll(true)
    setSending(true)
    setErrorMsg(null)
    try {
      const token = await getToken()
      const toResend = [...failures]
      const outcome = await runTargetedBatches(token, currentCampaignId, toResend, previewFilterLabel || (lastCampaign ? `${lastCampaign.year}_${lastCampaign.periode}` : 'campagne'))
      const stillFailedEmails = new Set(outcome.failures.map(f => f.email))
      setFailures(outcome.failures)

      if (currentCampaignId) {
        const finalStatus = stillFailedEmails.size > 0 ? 'partial_failure' : 'completed'
        await supabase
          .from('evaluation_campaigns')
          .update({ status: finalStatus })
          .eq('id', currentCampaignId)
      }
      await loadLastCampaign()
    } catch (err) {
      console.error(err)
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setResendingAll(false)
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    )
  }

  const recipientCount = previewRecipients?.length ?? null
  const exceedsDailyLimit = recipientCount !== null && recipientCount > DAILY_LIMIT
  const percent = progress && progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Envoi du formulaire d'évaluation
          </h3>
          <p className="text-sm text-gray-600">
            Envoyer aux parents l'invitation à répondre au questionnaire d'évaluation d'une période de plaines
            (un seul email par parent, même en cas de plusieurs inscriptions). Les inscriptions annulées et les enfants ayant participé moins de 3 jours sur la période sont exclus. Limite d'envoi&nbsp;:
            <strong> {DAILY_LIMIT} mails/jour</strong>.
          </p>
        </div>
      </div>

      {errorMsg && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{errorMsg}</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
        <div className="flex items-center gap-4 pb-2 border-b border-gray-100">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="eval-mode"
              value="bulk"
              checked={mode === 'bulk'}
              onChange={() => setMode('bulk')}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm font-medium text-gray-900">
              Envoi à tous les parents concernés
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="eval-mode"
              value="single"
              checked={mode === 'single'}
              onChange={() => setMode('single')}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm font-medium text-gray-900">
              Envoi à une seule adresse (test)
            </span>
          </label>
        </div>

        {mode === 'bulk' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année *</label>
              <select
                value={year}
                onChange={e => {
                  const v = e.target.value ? Number(e.target.value) : ''
                  setYear(v)
                  setPeriode('')
                  setCenterId('')
                  setPreviewRecipients(null)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">Sélectionner...</option>
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Période *</label>
              <select
                value={periode}
                onChange={e => {
                  setPeriode(e.target.value)
                  setCenterId('')
                  setPreviewRecipients(null)
                }}
                disabled={!year}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
              >
                <option value="">Sélectionner...</option>
                {availablePeriodes.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
              <select
                value={centerId}
                onChange={e => {
                  setCenterId(e.target.value)
                  setPreviewRecipients(null)
                }}
                disabled={!year || !periode}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
              >
                <option value="">Tous les centres</option>
                {filteredCenters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse email de destination *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={singleEmail}
                onChange={e => setSingleEmail(e.target.value)}
                placeholder="parent@exemple.be"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Si l'adresse correspond à un parent existant, son prénom sera utilisé automatiquement dans le message.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien du formulaire Google Form *
            </label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="url"
                value={formUrl}
                onChange={e => setFormUrl(e.target.value)}
                placeholder="https://forms.gle/..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien newsletter
            </label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="url"
                value={newsletterUrl}
                onChange={e => setNewsletterUrl(e.target.value)}
                placeholder="https://app.eclosion.be"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Objet du mail *</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Corps du mail *</label>
            <div className="text-xs text-gray-500">
              Variables : <code className="bg-gray-100 px-1 rounded">{'{prenom_parent}'}</code>{' '}
              <code className="bg-gray-100 px-1 rounded">{'{lien_formulaire}'}</code>
            </div>
          </div>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Le bouton "Donner mon avis", le bloc newsletter et la signature Éclosion sont ajoutés automatiquement au message.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          {mode === 'bulk' && (
            <button
              type="button"
              onClick={handlePreview}
              disabled={!canPreview}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Voir les destinataires
            </button>
          )}
          <button
            type="button"
            onClick={openSendConfirm}
            disabled={!canSend}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <Send className="h-4 w-4" />
            {mode === 'single' ? "Envoyer l'email de test" : 'Envoyer les invitations'}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={handleSaveDefaults}
              disabled={!canSave}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enregistrer le lien, l'objet et le corps comme valeurs par défaut"
            >
              {savingDefaults ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Enregistrer comme valeurs par défaut
            </button>
          )}
        </div>

        {recipientCount !== null && mode === 'bulk' && (
          <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
            exceedsDailyLimit ? 'bg-orange-50 text-orange-800 border border-orange-200'
              : 'bg-amber-50 text-amber-800 border border-amber-200'
          }`}>
            <Users className="h-4 w-4" />
            <span>
              <strong>{recipientCount}</strong> parent{recipientCount > 1 ? 's' : ''} unique{recipientCount > 1 ? 's' : ''} sera{recipientCount > 1 ? 'ont' : ''} contacté{recipientCount > 1 ? 's' : ''}.
              {exceedsDailyLimit && (
                <> Cela dépasse la limite journalière de {DAILY_LIMIT} mails ; envisagez de scinder par centre.</>
              )}
            </span>
          </div>
        )}
      </div>

      {progress && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              {sending ? (
                <Loader2 className="h-4 w-4 text-amber-600 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-600" />
              )}
              {sending ? 'Envoi en cours…' : 'Envoi terminé'}
            </h4>
            <div className="text-sm text-gray-700">
              <strong>{progress.done}</strong> / {progress.total}
            </div>
          </div>
          <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all duration-300 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              Envoyés&nbsp;: <strong>{progress.success}</strong>
            </div>
            <div className="flex items-center gap-2 text-red-700">
              <XCircle className="h-4 w-4" />
              Échecs&nbsp;: <strong>{progress.failure}</strong>
            </div>
            {sending && (
              <div className="flex items-center gap-2 text-gray-500">
                <Clock className="h-4 w-4" />
                Merci de patienter, l'envoi se fait par lots pour respecter les limites du serveur mail.
              </div>
            )}
          </div>
        </div>
      )}

      {singleResult && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h4 className="font-semibold text-gray-900 mb-3">Résultat du dernier envoi</h4>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              Email envoyé à <strong>{singleResult.recipient}</strong>.
              {singleResult.matched_user ? ' Le prénom du parent existant a été utilisé.' : ' Aucun parent trouvé pour cette adresse, envoi sans prénom.'}
            </div>
          </div>
        </div>
      )}

      {failures.length > 0 && (
        <div className="bg-white rounded-lg border border-red-200 p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Envois en échec ({failures.length})
              </h4>
              {lastCampaign && (
                <p className="text-xs text-gray-500 mt-1">
                  Campagne du {new Date(lastCampaign.created_at).toLocaleString('fr-BE')} —
                  {' '}{lastCampaign.periode} {lastCampaign.year}
                  {lastCampaign.center_name ? ` — ${lastCampaign.center_name}` : ' — tous centres'}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleResendAllFailures}
              disabled={resendingAll || sending}
              className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Renvoyer tous les échecs
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-red-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-red-800">Parent</th>
                  <th className="text-left px-3 py-2 font-medium text-red-800">Email</th>
                  <th className="text-left px-3 py-2 font-medium text-red-800">Erreur</th>
                  <th className="text-right px-3 py-2 font-medium text-red-800">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-100">
                {failures.map(f => {
                  const isResending = resending.has(f.email)
                  return (
                    <tr key={f.email}>
                      <td className="px-3 py-2 text-gray-900">
                        {((f.prenom || '') + ' ' + (f.nom || '')).trim() || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{f.email}</td>
                      <td className="px-3 py-2 text-red-700 text-xs">{f.error}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleResendOne(f)}
                          disabled={isResending || resendingAll || sending}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-amber-300 text-amber-700 text-xs rounded-md hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isResending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          Renvoyer
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {lastCampaign && failures.length === 0 && !sending && !progress && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800 flex items-center gap-3">
          <CheckCircle className="h-5 w-5" />
          Dernière campagne&nbsp;: {lastCampaign.periode} {lastCampaign.year}
          {lastCampaign.center_name ? ` — ${lastCampaign.center_name}` : ' — tous centres'} —
          {' '}{lastCampaign.total_recipients} destinataire{lastCampaign.total_recipients > 1 ? 's' : ''}, aucun échec.
        </div>
      )}

      {showPreviewModal && previewRecipients && (
        <PreviewModal
          recipients={previewRecipients}
          exceedsLimit={previewRecipients.length > DAILY_LIMIT}
          onClose={() => setShowPreviewModal(false)}
        />
      )}

      {showSendConfirm && (
        <ConfirmSendModal
          mode={mode}
          count={mode === 'single' ? 1 : (recipientCount ?? null)}
          singleEmail={singleEmail}
          onCancel={() => setShowSendConfirm(false)}
          onConfirm={handleSend}
        />
      )}
    </div>
  )
}

function PreviewModal({
  recipients,
  exceedsLimit,
  onClose,
}: {
  recipients: PreviewRecipient[]
  exceedsLimit: boolean
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-500" />
            Destinataires ({recipients.length})
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        {exceedsLimit && (
          <div className="mx-6 mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Attention&nbsp;: {recipients.length} envois dépassent la limite journalière de {DAILY_LIMIT}.
            Envisagez de filtrer par centre.
          </div>
        )}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {recipients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun destinataire pour ces critères.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Parent</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Email</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Inscriptions</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Jours de présence</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Centres</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recipients.map(r => (
                  <tr key={r.email}>
                    <td className="px-3 py-2 text-gray-900">
                      {(r.prenom || '') + ' ' + (r.nom || '')}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{r.email}</td>
                    <td className="px-3 py-2 text-gray-700">{r.kids_count}</td>
                    <td className="px-3 py-2 text-gray-700">{r.presence_days ?? '-'}</td>
                    <td className="px-3 py-2 text-gray-700">{r.centers.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmSendModal({
  mode,
  count,
  singleEmail,
  onCancel,
  onConfirm,
}: {
  mode: 'bulk' | 'single'
  count: number | null
  singleEmail: string
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Confirmer l'envoi
        </h3>
        {mode === 'single' ? (
          <p className="text-sm text-gray-700">
            Vous êtes sur le point d'envoyer un email de test à <strong>{singleEmail}</strong>. Continuer ?
          </p>
        ) : (
          <p className="text-sm text-gray-700">
            Vous êtes sur le point d'envoyer l'invitation d'évaluation
            {count !== null ? <> à <strong>{count}</strong> parent{count > 1 ? 's' : ''}</> : ''}.
            Un seul mail sera envoyé par parent, même en cas d'inscriptions multiples. L'envoi se fait par lots pour respecter les limites du serveur mail. Continuer ?
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            <Send className="h-4 w-4" />
            Envoyer
          </button>
        </div>
      </div>
    </div>
  )
}

function isValidEmail(e: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())
}

function isValidUrl(u: string): boolean {
  try {
    const parsed = new URL(u.trim())
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}
